import { Nullable } from "@simplyk/common";

import { getRateSalesWindow, getRateSalesWindowStatus, RateSalesWindowStatus } from "@/common/helpers/rateSalesWindow";
import { OccurrenceWithRatesOutput, RateOutput } from "@/common/types/trpc";

const AUCTION_SORT_ORDER: Record<"isOutbid" | "isWinning" | "YouDidNotBidYet", number> = {
  isOutbid: 0,
  isWinning: 1,
  YouDidNotBidYet: 2,
};

type GetValidRatesSortedParams = {
  rates: RateOutput[];
  isAuction: boolean;
  commandBidsRateIdsMap?: Record<string, { amount: number }> | null;
  highestBidsMapByRateId?: Record<string, { amount?: number | null; effectiveEndUtc?: string | null }>;
  selectedOccurrenceWithRates: Nullable<OccurrenceWithRatesOutput>;
  soldOutItemToBottom: boolean;
  memberOnlyRatesToTop?: boolean;
  now?: Date;
};

export function getValidRatesSorted({
  rates,
  isAuction,
  commandBidsRateIdsMap,
  highestBidsMapByRateId,
  selectedOccurrenceWithRates,
  soldOutItemToBottom,
  memberOnlyRatesToTop,
  now = new Date(),
}: GetValidRatesSortedParams): RateOutput[] {
  const filtered = rates.filter((rate) => {
    if (rate.isHidden) {
      return false;
    }
    if (rate.isAddOn) {
      return false;
    }
    return true;
  });

  const salesWindowByRateId = new Map(filtered.map((rate) => [rate.id, getRateSalesWindow(rate, rates)]));
  const salesWindowStatusByRateId = new Map<string, RateSalesWindowStatus>(
    filtered.map((rate) => [rate.id, getRateSalesWindowStatus(salesWindowByRateId.get(rate.id)!, now)])
  );

  // Apply Auction sort if it's an auction form and we have the bid maps
  if (isAuction && highestBidsMapByRateId) {
    return filtered.sort((a, b) => {
      const aRateClosed = isRateClosedForSort(a.id, highestBidsMapByRateId);
      const bRateClosed = isRateClosedForSort(b.id, highestBidsMapByRateId);
      if (aRateClosed !== bRateClosed) {
        return aRateClosed ? 1 : -1;
      }

      if (commandBidsRateIdsMap) {
        const aStatus = getAuctionBidStatus(a.id, commandBidsRateIdsMap, highestBidsMapByRateId);
        const bStatus = getAuctionBidStatus(b.id, commandBidsRateIdsMap, highestBidsMapByRateId);

        const statusComparison = AUCTION_SORT_ORDER[aStatus] - AUCTION_SORT_ORDER[bStatus];
        if (statusComparison !== 0) {
          return statusComparison;
        }
      }

      return (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
    });
  }

  const getScheduleSortLevel = (rate: RateOutput): number => {
    const salesWindowStatus = salesWindowStatusByRateId.get(rate.id);
    if (salesWindowStatus === "ended") {
      return 3;
    }
    if (soldOutItemToBottom && isSoldOutForSort(rate, selectedOccurrenceWithRates)) {
      return 2;
    }
    if (salesWindowStatus === "notStarted") {
      return 1;
    }
    return 0;
  };

  return filtered.sort((a, b) => {
    const levelA = getScheduleSortLevel(a);
    const levelComparison = levelA - getScheduleSortLevel(b);
    if (levelComparison !== 0) {
      return levelComparison;
    }

    if (levelA === 3) {
      const endTimeA = salesWindowByRateId.get(a.id)?.endDate?.getTime() ?? 0;
      const endTimeB = salesWindowByRateId.get(b.id)?.endDate?.getTime() ?? 0;
      if (endTimeA !== endTimeB) {
        return endTimeB - endTimeA;
      }
    }

    if (memberOnlyRatesToTop) {
      const aMemberOnly = Boolean(a.isMemberOnly);
      const bMemberOnly = Boolean(b.isMemberOnly);
      if (aMemberOnly !== bMemberOnly) {
        return aMemberOnly ? -1 : 1;
      }
    }

    return (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
  });
}

const isSoldOutForSort = (rate: RateOutput, selectedOccurrenceWithRates: Nullable<OccurrenceWithRatesOutput>) => {
  const currentRateByOccurrence = selectedOccurrenceWithRates?.rates.find((r) => r.rateId === rate.id);
  const remainingRateTickets = currentRateByOccurrence?.remainingTickets ?? Infinity;
  return remainingRateTickets <= 0;
};

const isRateClosedForSort = (
  rateId: string,
  highestBidsMapByRateId: Record<string, { effectiveEndUtc?: string | null }>
): boolean => {
  const effectiveEndUtc = highestBidsMapByRateId[rateId]?.effectiveEndUtc;
  return Boolean(effectiveEndUtc) && new Date(effectiveEndUtc!) < new Date();
};

export function getAuctionBidStatus(
  rateId: string,
  commandBidsRateIdsMap: Record<string, { amount: number }>,
  highestBidsMapByRateId: Record<string, { amount?: number | null }>
): "isOutbid" | "isWinning" | "YouDidNotBidYet" {
  const currentBidAmount = commandBidsRateIdsMap?.[rateId]?.amount;
  const highestBidAmount = highestBidsMapByRateId?.[rateId]?.amount;
  const hasABid = currentBidAmount !== undefined && currentBidAmount !== null;
  const isWinning = hasABid && currentBidAmount === highestBidAmount;

  if (!hasABid) {
    return "YouDidNotBidYet";
  }
  return isWinning ? "isWinning" : "isOutbid";
}
